import menu2Fill from "@iconify/icons-eva/menu-2-fill";
import searchFill from "@iconify/icons-eva/search-fill";
import { Icon } from "@iconify/react";
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
// material
import { alpha, styled } from "@material-ui/core/styles";
// components
import { MHidden } from "../../components/@material-extend";
import ContactSearch from "../../components/ContactSearch";
// hooks
import useCollapseDrawer from "../../hooks/useCollapseDrawer";
import AccountPopover from "./AccountPopover";
import LanguagePopover from "./LanguagePopover";
import { useModal } from "../../hooks/useModal";
import FeatureUpdatesModal from "../../components/FeatureUpdatesModal";
import FeatureUpdatePopup from "../../components/featureUpdates/FeatureUpdatePopup";
import { useFeatureUpdates } from "hooks/useFeatureUpdates";
import { useTranslation } from "react-i18next";
import { useState } from "react";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 64;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: "none",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)", // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  [theme.breakpoints.up("lg")]: {
    width: `calc(100% - ${DRAWER_WIDTH}px)`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up("lg")]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5)
  }
}));

// ----------------------------------------------------------------------

type DashboardNavbarProps = {
  onOpenSidebar: VoidFunction;
};

export default function DashboardNavbar({ onOpenSidebar }: DashboardNavbarProps) {
  const { isCollapse } = useCollapseDrawer();
  const { open, openModal, closeModal } = useModal();
  const { hasUpdates, hasUnseenUpdates } = useFeatureUpdates();
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  function handleSearchToggle() {
    setIsSearchOpen(!isSearchOpen);
  }

  function handleSearchClose() {
    setIsSearchOpen(false);
  }

  return (
    <RootStyle
      sx={{
        ...(isCollapse && {
          width: { lg: `calc(100% - ${COLLAPSE_WIDTH}px)` }
        })
      }}
    >
      <ToolbarStyle>
        <MHidden width="lgUp">
          <IconButton onClick={onOpenSidebar} sx={{ mr: 1, color: "text.primary" }}>
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>

        <Box sx={{ flexGrow: 1 }} />

        {/* Search - Desktop */}
        {!isMobile && (
          <Box sx={{ position: "relative", mr: 2, minWidth: isSearchOpen ? 600 : "auto" }}>
            <ContactSearch isOpen={isSearchOpen} onClose={handleSearchClose} />
            {!isSearchOpen && (
              <IconButton
                onClick={handleSearchToggle}
                sx={{
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "action.hover"
                  }
                }}
              >
                <Icon icon={searchFill} />
              </IconButton>
            )}
          </Box>
        )}

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {/* Search - Mobile */}
          {isMobile && (
            <IconButton
              onClick={handleSearchToggle}
              sx={{
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "action.hover"
                }
              }}
            >
              <Icon icon={searchFill} />
            </IconButton>
          )}

          {hasUpdates && (
            <IconButton onClick={openModal} sx={{ color: "grey" }}>
              <Badge color="success" variant="dot" invisible={!hasUnseenUpdates}>
                <Icon icon="mdi:megaphone" />
              </Badge>
            </IconButton>
          )}
          <LanguagePopover />
          {/* <NotificationsPopover /> */}
          <AccountPopover />
        </Stack>
      </ToolbarStyle>

      {/* Mobile Search Overlay */}
      {isMobile && isSearchOpen && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            backgroundColor: "background.paper",
            p: 2
          }}
        >
          <ContactSearch isOpen={isSearchOpen} onClose={handleSearchClose} />
        </Box>
      )}

      {hasUpdates && <FeatureUpdatesModal open={open} onClose={closeModal} />}
      {/* A big update Haim approved, opened by itself once (featureUpdatePopups.ts). */}
      <FeatureUpdatePopup onOpenAll={openModal} />
    </RootStyle>
  );
}
